import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useP2PStore from '../stores/p2pStore';

const GameRoom = () => {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isHost = location.pathname.startsWith('/host/');
  const [gameState, setGameState] = useState('waiting');
  const [myChoiceData, setMyChoiceData] = useState(null); // { choice, timestamp }
  const [opponentChoiceData, setOpponentChoiceData] = useState(null); // { choice, timestamp }
  const [result, setResult] = useState(null);
  const { 
    initialize, 
    connect, 
    disconnect, 
    sendMessage, 
    setMessageHandler,
    setDisconnectHandler,
    isPeerReady, 
    isConnected 
  } = useP2PStore();

  const handleChoice = (choice) => {
    const now = Date.now();
    const myData = { choice, timestamp: now };
    setMyChoiceData(myData);
    
    sendMessage(
      new TextEncoder().encode(
        JSON.stringify({ type: 'choice', choice, timestamp: now })
      )
    );
  };

  const handlePlayAgain = () => {
    sendMessage(
      new TextEncoder().encode(JSON.stringify({ type: 'playAgain' }))
    );
    resetGame();
  };

  const resetGame = () => {
    setGameState('choosing');
    setMyChoiceData(null);
    setOpponentChoiceData(null);
    setResult(null);
  };

  // 승패 판정 함수
  const determineWinner = (myChoice, opponentChoice) => {
    if (myChoice === opponentChoice) return 'draw';
    
    const winConditions = {
      rock: 'scissors',
      paper: 'rock',
      scissors: 'paper'
    };
    
    return winConditions[myChoice] === opponentChoice ? 'win' : 'lose';
  };

  // 초기 연결 설정을 위한 useEffect
  useEffect(() => {
    if (!isHost) {
      initialize(
        "https://b7c5eyyip5qvb5uyud2w3jowce0jjijc.lambda-url.ap-northeast-2.on.aws/",
        "handsomecoder58!"  
      );
      disconnect();
      connect(roomId);
    }
  }, [roomId, isHost]);

  // 승패 판정을 위한 useEffect 추가
  useEffect(() => {
    if (myChoiceData && opponentChoiceData) {
      const result = determineWinner(myChoiceData.choice, opponentChoiceData.choice);
      setResult(result);
      setGameState('result');
    }
  }, [myChoiceData, opponentChoiceData]);

  // 메시지 핸들러 수정
  useEffect(() => {
    let isSubscribed = true;

    const handleMessage = (data) => {
      if (!isSubscribed) return;
      
      const message = JSON.parse(new TextDecoder().decode(data));
      console.log("메시지 수신:", message);
      
      if (message.type === 'choice') {
        setOpponentChoiceData({ 
          choice: message.choice, 
          timestamp: message.timestamp 
        });
      } else if (message.type === 'playAgain') {
        resetGame();
      }
    };

    setMessageHandler(handleMessage);

    return () => {
      isSubscribed = false;
    };
  }, []);

  useEffect(() => {
    // isPeerReady 상태가 true가 되면 choosing 상태로 전환
    if (isConnected && isPeerReady) {
      setGameState('choosing');
    }
  }, [isConnected, isPeerReady]);

  // Add disconnect handler
  useEffect(() => {
    const handleDisconnect = () => {
      navigate('/disconnected');
    };

    setDisconnectHandler(handleDisconnect);

    return () => {
      setDisconnectHandler(null);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 px-4">
      <div className="bg-white/10 backdrop-blur-lg p-4 sm:p-10 rounded-2xl shadow-2xl w-full max-w-2xl border border-white/20 relative min-h-[500px]">
        {isHost && (
          <button
            onClick={() => navigate('/')}
            className="absolute top-4 right-4 px-3 py-1 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-300 text-sm sm:text-base"
          >
            Home
          </button>
        )}
        <h1 className="text-2xl sm:text-4xl font-bold mb-6 sm:mb-10 text-center text-white mt-8">
          Game Room: {roomId}
        </h1>
        <div className="text-white text-center mb-4">
          You are the {isHost ? 'Host' : 'Guest'}
        </div>
        {!isPeerReady && (
          <div className="text-white text-center">
            Waiting for opponent to join...
          </div>
        )}
        {isPeerReady && gameState === 'choosing' && (
          <div className="flex flex-col items-center gap-4">
            <div className="text-white mb-4">Make your choice!</div>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              {['rock', 'paper', 'scissors'].map((ch) => (
                <button
                  key={ch}
                  onClick={() => handleChoice(ch)}
                  disabled={myChoiceData !== null}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-300 disabled:opacity-50 text-sm sm:text-base"
                >
                  {ch.toUpperCase()}
                </button>
              ))}
            </div>
            {myChoiceData && !opponentChoiceData && (
              <div className="text-white mt-4">
                Waiting for opponent's choice...
              </div>
            )}
          </div>
        )}
        {gameState === 'result' && (
          <div className="text-white text-center">
            <div className="text-2xl mb-4">
              {result === 'win' && 'You Win! 🎉'}
              {result === 'lose' && 'You Lose! 😢'}
              {result === 'draw' && "It's a Draw! 🤝"}
            </div>
            <div className="mb-4">
              Your choice: {myChoiceData && myChoiceData.choice.toUpperCase()}
              <br />
              Opponent's choice: {opponentChoiceData && opponentChoiceData.choice.toUpperCase()}
            </div>
            <button
              onClick={handlePlayAgain}
              className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-300"
            >
              Play Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameRoom;